import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useParams } from 'react-router-dom';
import { useGetGoogleBusinessReview } from '../../../queries';
import { THEME } from '../../components-v2/Theme/theme';
import { AppBarNavigationItemPortalDestinationId } from '../../components/AppBar';
import { NavigationBreadcrumbsBar } from '../../components/NavigationBreadcrumbsBar';
import { Portal } from '../../components/Portal';
import { TestimonialImageEditor } from '../../components/TestimonialImageEditor';
import { TestimonialSource } from '../../components/TestimonialImageEditor/types';
import { styleSheet } from './styles';
import {
	AppDrawer,
	StickyAppDrawerContent,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
} from '../../components-v2/Drawer';
import { DefaultUniversalAssistantChat } from '../templates/AssistantChat';

interface TestimonialLocationState {
	testimonialSource: TestimonialSource;
	model?: any;
}

export interface TestimonialImageEditorModalContentProps {
	accountId?: string;
	testimonial: TestimonialSource;
	impersonationContext?: Api.IImpersonationContext;
	containerStyles?: React.CSSProperties[];
	builderStyles?: React.CSSProperties[];
	onPostCreated?: () => void;
}

/**
 * Container for TestimonialImageEditor when rendered inside a modal.
 * Establishes proper container context (containerType: inline-size) so the editor's
 * container queries respond to the modal width rather than the viewport.
 */
export const TestimonialImageEditorModalContent: React.FC<TestimonialImageEditorModalContentProps> = ({
	testimonial,
	containerStyles,
	builderStyles,
	impersonationContext,
	accountId,
	onPostCreated,
}) => (
	<div className={css(styleSheet.modalContent)}>
		<TestimonialImageEditor
			accountId={accountId}
			impersonationContext={impersonationContext}
			testimonial={testimonial}
			containerStyles={containerStyles}
			builderStyles={builderStyles}
			onPostCreated={onPostCreated}
		/>
	</div>
);

const TestimonialImageEditorContainerInner: React.FC = () => {
	const { id } = useParams<{ id?: string }>();
	const location = useLocation<TestimonialLocationState>();
	const state = location.state || { testimonialSource: null };
	const isMobile = useMediaQuery({ maxWidth: THEME.breakpoints.md });

	const {
		data: googleReview,
		isPending,
		error,
	} = useGetGoogleBusinessReview({
		reviewId: id || '',
		enabled: !!id,
		refetchOnWindowFocus: false,
	});

	const testimonialSource: TestimonialSource | null = React.useMemo(() => {
		if (state.testimonialSource) {
			return state.testimonialSource;
		}

		if (googleReview && id) {
			const activeGoogleReview = { ...googleReview, includeGeneratedReply: false };
			return {
				type: 'google_review',
				data: activeGoogleReview,
			};
		}

		return null;
	}, [state.testimonialSource, googleReview, id]);

	if (isPending && id) {
		return (
			<>
				<h2>Loading testimonial data...</h2>
			</>
		);
	}

	if (error && id) {
		return (
			<>
				<h2>Error: Failed to load Google review</h2>
				<p>Please check the review ID and try again.</p>
			</>
		);
	}

	if (!testimonialSource) {
		return (
			<>
				<h2>Error: No testimonial data provided</h2>
				<p>Please provide a review ID in the URL or navigate from a review.</p>
			</>
		);
	}

	return (
		<>
			{!isMobile && (
				<Portal destination={AppBarNavigationItemPortalDestinationId}>
					<NavigationBreadcrumbsBar
						currentLocationName='Custom Testimonial'
						pathComponentNameProvider={pathComponent =>
							pathComponent === 'testimonial-image-editor' && id ? null : 'Custom Testimonial'
						}
					/>
				</Portal>
			)}
			<TestimonialImageEditor testimonial={testimonialSource} />
		</>
	);
};

const TestimonialImageEditorContainer: React.FC = () => {
	return (
		<div className={css(styleSheet.container)}>
			<TestimonialImageEditorContainerInner />
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>
		</div>
	);
};

export { TestimonialImageEditorContainer };
